import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Button, Dialog, useDialogStore } from '@ariakit/react';
import { Filter, X, Check } from 'lucide-react';
import type { Column, Table } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { multiSelect } from '../filters';
import { FilterStyle } from '../types';

interface ColumnFilterProps<TData> {
  column: Column<TData>;
  table: Table<TData>;
  filterStyle?: FilterStyle;
}

export default function ColumnFilter<TData>({ column, table, filterStyle = 'select' }: ColumnFilterProps<TData>) {
  const dialog: any = useDialogStore();
  const [searchValue, setSearchValue] = useState('');
  const [selectedValues, setSelectedValues] = useState<Set<string> | null>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const distinctValues = useMemo(() => {
    const values = new Map<string, number>();
    
    // For select mode: use faceted values which respect other column filters
    // For solo-check mode: use all pre-filtered values
    if (filterStyle === 'select') {
      // Use faceted values - this automatically excludes this column's filter
      const faceted = column.getFacetedUniqueValues();
      faceted.forEach((count, value) => {
        const displayValue = value == null || String(value).trim() === '' ? '(Blank)' : String(value);
        values.set(displayValue, count);
      });
    } else {
      // Solo-check mode: show all values regardless of filters
      const rowsToCheck = table.getPreFilteredRowModel().rows;
      rowsToCheck.forEach(row => {
        const value = row.getValue(column.id);
        const displayValue = value == null || String(value).trim() === '' ? '(Blank)' : String(value);
        values.set(displayValue, (values.get(displayValue) || 0) + 1);
      });
    }
    
    return Array.from(values.entries())
      .sort((a, b) => {
        if (a[0] === '(Blank)') return 1;
        if (b[0] === '(Blank)') return -1;
        return a[0].localeCompare(b[0]);
      });
  }, [column, table, filterStyle, column.getFacetedUniqueValues()]);
  
  const filteredValues = useMemo(() => {
    if (!searchValue) return distinctValues;
    
    const search = searchValue.toLowerCase();
    return distinctValues.filter(([value]) => 
      value.toLowerCase().includes(search)
    );
  }, [distinctValues, searchValue]);
  
  // Reset search when dialog opens
  useEffect(() => {
    if (dialog.open) {
      setSearchValue('');
    }
  }, [dialog.open]);

  // Initialize selected values when we have distinct values
  useEffect(() => {
    if (distinctValues.length === 0) return;
    if (selectedValues !== null) return; // Already initialized
    
    const currentFilter = column.getFilterValue() as string[] | undefined;
    
    if (Array.isArray(currentFilter)) {
      // If there's an active filter array, use those values
      setSelectedValues(new Set(currentFilter));
    } else {
      // No filter (undefined) means all values should be selected
      setSelectedValues(new Set(distinctValues.map(([value]) => value)));
    }
  }, [distinctValues, column, selectedValues]);
  
  const handleSelectAll = () => {
    if (filterStyle === 'select') {
      setSelectedValues(new Set(filteredValues.map(([value]) => value)));
    } else {
      setSelectedValues(new Set(distinctValues.map(([value]) => value)));
    }
  };
  
  const handleSelectNone = () => {
    if (filterStyle === 'select') {
      const currentSelected = selectedValues || new Set(distinctValues.map(([v]) => v));
      const newSelected = new Set(currentSelected);
      filteredValues.forEach(([value]) => {
        newSelected.delete(value);
      });
      setSelectedValues(newSelected);
    } else {
      setSelectedValues(new Set());
    }
  };

  const handleToggleValue = (value: string) => {
    // Initialize if needed
    const currentSelected = selectedValues || new Set(distinctValues.map(([v]) => v));
    const newSelected = new Set(currentSelected);
    if (newSelected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    setSelectedValues(newSelected);
  };
  
  const handleApply = () => {
    let valuesToApply: string[] | undefined;
    
    // Get the actual selected values (initialize if needed)
    const currentSelected = selectedValues || new Set(distinctValues.map(([v]) => v));

    // Always use the checked values, regardless of search
    if (currentSelected.size === 0) {
      valuesToApply = [];
    } else if (filterStyle === 'solo-check' && currentSelected.size === distinctValues.length) {
      // Only clear filter in solo-check mode when all values are selected
      valuesToApply = undefined;
    } else if (filterStyle === 'select') {
      const search = searchValue.toLowerCase();
      if (!search) {
        valuesToApply = Array.from(currentSelected);
      } else {
        valuesToApply = Array.from(currentSelected).filter((value: string) => value.toLowerCase().includes(search));
      }
    } else {
      valuesToApply = Array.from(currentSelected);
    }
    
    column.setFilterValue(valuesToApply);
    dialog.hide();
  };
  
  const handleClear = () => {
    setSearchValue("");
    if (filterStyle === 'select') {
      // In select mode, clearing means selecting all currently visible values
      const allVisibleValues = distinctValues.map(([value]) => value);
      column.setFilterValue(undefined); // Clear filter to show all
      setSelectedValues(new Set(allVisibleValues));
    } else {
      // In solo-check mode, clearing means removing the filter entirely
      column.setFilterValue(undefined);
      setSelectedValues(new Set(distinctValues.map(([value]) => value)));
    }
    dialog.hide();
  };
  
  const isFiltered = column.getIsFiltered();
  
  // Virtual scrolling setup
  const virtualizer = useVirtualizer({
    count: filteredValues.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40, // Estimated height of each checkbox item
    overscan: 5,
  });
  
  return (
    <>
      <Button
        onClick={dialog.toggle}
        className={`p-1 rounded hover:bg-gray-100 ${isFiltered ? 'text-blue-600' : 'text-gray-400'}`}
        aria-label={`Filter ${column.id}`}
        title="Click to filter"
      >
        <Filter className="h-4 w-4" />
      </Button>
      
      <Dialog
        initialFocus={inputRef}
        store={dialog}
        className="fixed inset-4 z-50 flex items-center justify-center"
        backdrop={<div className="fixed inset-0 bg-black/30" />}
        data-testid="filter-dialog"
      >
        <div className="bg-white rounded-lg shadow-xl w-96 max-h-[600px] flex flex-col">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold">Filter: {column.id}</h2>
            <Button
              onClick={dialog.hide}
              className="p-1 rounded hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="px-4 py-3 border-b">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search values..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="px-4 py-2 border-b flex gap-2">
            <button
              onClick={handleSelectAll}
              className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
            >
              Select All
            </button>
            <button
              onClick={handleSelectNone}
              className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
            >
              Clear All
            </button>
          </div>
          
          <div 
            ref={parentRef}
            className="flex-1 overflow-auto px-4 py-2 min-w-0"
            style={{ maxHeight: '400px' }}
          >
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const [value, count] = filteredValues[virtualItem.index];
                return (
                  <div
                    key={virtualItem.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    <label className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer min-w-0">
                      <input
                        type="checkbox"
                        checked={
                          selectedValues === null 
                            ? (column.getFilterValue() === undefined || (Array.isArray(column.getFilterValue()) && (column.getFilterValue() as string[]).includes(value)))
                            : selectedValues.has(value)
                        }
                        onChange={() => handleToggleValue(value)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                      />
                      <span 
                        className="flex-1 truncate min-w-0 max-w-full"
                        title={value}
                      >
                        {value}
                      </span>
                      <span 
                        className="text-sm text-gray-500 flex-shrink-0 ml-2"
                        title={`Count: ${count}`}
                      >
                        ({count})
                      </span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="px-4 py-3 border-t">
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleClear}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Clear Filter
              </button>
              <button
                onClick={handleApply}
                className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
